import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";
import { COMPANY_STATUS_OPTIONS } from "../constants";
import {
  ALL_STAFF_ROLES,
  ALL_STAFF_AND_CLIENT_ROLES,
} from "@west-tennessee-consulting/hub-roles";

export const companyExtendedFields = defineFieldConfigMap({
    // FIELD: domain
    domain: {
      type: "text",
      label: "Domain",
      mobileDetailSectionId: "contact",
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "contains",
      width: 180,
      minWidth: 150,
      maxWidth: 250,
      placeholder: "example.com",
      icon: "Globe",
      allowedRoles: [...ALL_STAFF_AND_CLIENT_ROLES],
      graphqlSelector: "domain",
    },
    // FIELD: organization_revenue
    organization_revenue: {
      type: "text",
      label: "Organization Revenue",
      mobileDetailSectionId: "commercial",
      sortable: true,
      filterable: true,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "range",
      width: 160,
      minWidth: 140,
      maxWidth: 200,
      placeholder: "Revenue amount",
      icon: "DollarSign",
      // Internal/financial — staff only.
      allowedRoles: [...ALL_STAFF_ROLES],
      graphqlSelector: "organization_revenue",
    },
    // FIELD: quickbooks_customer_ref
    quickbooks_customer_ref: {
      type: "text",
      label: "QB Customer Ref",
      mobileDetailSectionId: "metadata",
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: false, // Read-only from QuickBooks
      filterType: "contains",
      width: 160,
      minWidth: 140,
      maxWidth: 220,
      placeholder: "QB reference ID",
      icon: "Calculator",
      // Internal/financial — staff only.
      allowedRoles: [...ALL_STAFF_ROLES],
      graphqlSelector: "quickbooks_customer_ref",
    },
    // FIELD: current_technologies
    current_technologies: {
      type: "json",
      label: "Technologies",
      mobileDetailSectionId: "commercial",
      sortable: false,
      filterable: true,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: true,
      filterType: "contains",
      width: 200,
      minWidth: 150,
      maxWidth: 300,
      placeholder: "Tech stack data",
      icon: "Code",
      // Internal/financial — staff only.
      allowedRoles: [...ALL_STAFF_ROLES],
      graphqlSelector: "current_technologies",
    },
    // FIELD: social_links
    social_links: {
      type: "json",
      label: "Social Links",
      mobileDetailSectionId: "commercial",
      sortable: false,
      filterable: false,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: true,
      width: 200,
      minWidth: 150,
      maxWidth: 300,
      placeholder: "Social media links",
      icon: "Share2",
      // Internal/financial — staff only.
      allowedRoles: [...ALL_STAFF_ROLES],
      graphqlSelector: "social_links",
    },
    // FIELD: employee_size
    employee_size: {
      type: "json",
      label: "Employee Size",
      mobileDetailSectionId: "commercial",
      sortable: false,
      filterable: true,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: true,
      width: 150,
      minWidth: 120,
      maxWidth: 200,
      placeholder: "Employee count data",
      icon: "Users",
      // Internal/financial — staff only.
      allowedRoles: [...ALL_STAFF_ROLES],
      graphqlSelector: "employee_size",
    },
    // FIELD: technical_identifiers
    // Staff-only bag of technical system identifiers SUPPORT ENGINEERS use to
    // service the company (M365 tenant, Azure subscription, AWS account, …).
    // ONE extensible jsonb column — array of {system,label,value,notes}. Not for
    // secrets/credentials. Written via the Meridian technical-identifier tools or
    // this inline json editor. UI-gated to staff (excludes wtc_crm + client_user).
    technical_identifiers: {
      // Bespoke structured editor (rows of {system,label,value,notes}) — same
      // "array" + customComponent plumbing as contact email_aliases/phone_aliases,
      // but the value is an object array; see TechnicalIdentifiersEditor and the
      // customComponent branches in the form schema/defaults/cleaner.
      type: "array",
      customComponent: "TechnicalIdentifiersEditor",
      label: "Technical Identifiers",
      mobileDetailSectionId: "metadata",
      sortable: false,
      filterable: false,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: true,
      formSpan: 3,
      arrayConfig: { maxItems: 50, allowCustom: true },
      width: 220,
      minWidth: 160,
      maxWidth: 320,
      icon: "Fingerprint",
      // Support-engineering data — staff only (admins + employees), never sales/clients.
      allowedRoles: [...ALL_STAFF_ROLES],
      graphqlSelector: "technical_identifiers",
    },
    // FIELD: tags
    tags: {
      type: "tags",
      label: "Tags",
      mobileDetailSectionId: "basic",
      sortable: false,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: true,
      bulkEditable: true,
      filterType: "contains",
      width: 200,
      minWidth: 150,
      maxWidth: 300,
      placeholder: "Add tags...",
      icon: "Tag",
      // Role-based access control - visible to all roles
      allowedRoles: [...ALL_STAFF_AND_CLIENT_ROLES],
      showOnCard: true,
      cardSpan: 3,
      graphqlSelector: "tags",
    }
});
